/*
 * Voyado card behaviour — replicates assets/product-card.js's initCard,
 * scoped to [data-voyado-card]. The Voyado cards are byte-identical mirrors of
 * the Liquid product-card, so we reuse the SAME transition/drawer/swatch
 * helpers (window.BroraProductCardVariant) and the SAME wishlist binder
 * (window.BroraProductCardWishlist) — only init is replicated here so the
 * parallel architecture stays intact.
 *
 * Cards are injected after load (PLP grid pages, drawer results/trending), so
 * init is idempotent (READY-flag guarded): an initial initAll(document), plus a
 * voyado:cards-rendered listener that inits each newly inserted root.
 *
 * Add-to-bag rides add-to-bag.js: the size click dispatches product-card:add
 * { variantId, card }; add-to-bag fires cart:added { trigger: card }; and
 * voyado-client.js reads trigger.dataset.voyadoTicket. To fire exactly
 * ONE notify per add, the size click copies the pill's data-voyado-ticket-value
 * onto the card root's data-voyado-ticket at click time (after mousedown, so the
 * mousedown click-notify never sees it — no double-fire).
 */
;(function () {
  'use strict';

  const READY = 'data-voyado-card-ready';
  const SWIPE = 40;
  const LG = window.matchMedia('(min-width: 1024px)');

  function initCard(card) {
    if (card.hasAttribute(READY)) return;
    // Read the shared helpers lazily at init (cards render after load) so we
    // don't depend on product-card-variant.js (an ES module) having executed
    // before this classic defer script.
    const V = window.BroraProductCardVariant || {};
    card.setAttribute(READY, '');

    const gallery = card.querySelector('[data-card-gallery]');
    const galleryScroll = (gallery && gallery.querySelector('[data-card-gallery-scroll]')) || gallery;
    const slides = galleryScroll ? Array.from(galleryScroll.querySelectorAll('[data-card-image]')) : [];
    const counter = card.querySelector('[data-card-gallery-current]');
    const counterWrap = card.querySelector('[data-card-gallery-counter]');
    const trigger = card.querySelector('[data-quick-add-trigger]');
    const drawer = card.querySelector('[data-quick-add-drawer]');
    const sizeBtns = Array.from(card.querySelectorAll('[data-quick-add-size]'));
    const swatches = Array.from(card.querySelectorAll('[data-swatch]'));
    const wishlist = card.querySelector('[data-wishlist]');
    const galleryEnabled = card.hasAttribute('data-gallery-enabled') && slides.length > 1;
    let current = 0;

    if (LG.matches && slides.length > 1) {
      slides.forEach((s, i) => {
        s.classList.add(i === 0 ? 'translate-x-0' : 'translate-x-full');
        s.classList.add(i === 0 ? 'opacity-100' : 'opacity-0');
      });
    }

    // Slide 0 is always needed. On desktop slide 1 is too: it is the mouseenter crossfade target, and a
    // fade into an unhydrated image reads as a broken card. Everything past that waits for a real
    // navigation. Below lg the scroller has real geometry, so an observer does the deferral properly.
    // galleryEnabled gates the slide-1 warm: on a card with the gallery disabled the hover preview is
    // never bound, so pre-fetching slide 1 there is one wasted image per card (P2-5).
    V.hydrateSlide?.(slides[0]);
    if (LG.matches) {
      if (galleryEnabled && slides.length > 1) V.hydrateSlide?.(slides[1]);
    } else {
      V.observeHydration?.(galleryScroll, slides);
    }

    function show(i, { mode = 'slide', dir } = {}) {
      if (!slides.length || i < 0 || i >= slides.length || i === current) return;
      // Promote the incoming slide before the transition starts, then warm the wrapped neighbours so the
      // next arrow press is instant too.
      V.hydrateSlide?.(slides[i]);
      if (LG.matches && slides.length > 2) {
        V.hydrateSlide?.(slides[(i + 1) % slides.length]);
        V.hydrateSlide?.(slides[(i - 1 + slides.length) % slides.length]);
      }
      if (!LG.matches) {
        current = i;
        if (counter) counter.textContent = String(i + 1);
        galleryScroll.scrollTo({ left: slides[i].offsetLeft, behavior: mode === 'fade' ? 'auto' : 'smooth' });
        return;
      }
      const outgoing = slides[current];
      const incoming = slides[i];
      // Loop wraps the index, so honour the caller's direction when given; fall back
      // to positional direction (hover previews pass no dir).
      const forward = dir === undefined ? i > current : dir;
      if (mode === 'slide') {
        V.showSlide(outgoing, incoming, forward);
      } else {
        V.showFade(outgoing, incoming);
      }
      outgoing.setAttribute('aria-hidden', 'true'); outgoing.toggleAttribute('inert', true);
      incoming.removeAttribute('aria-hidden'); incoming.removeAttribute('inert');
      current = i;
      if (counter) counter.textContent = String(i + 1);
    }

    if (galleryEnabled) {
      // Gallery loops: prev on the first image wraps to the last, next on the last
      // wraps to the first. Direction is passed explicitly so the slide animates the
      // intuitive way across the wrap (reuses the transform transitions — no clones).
      const prev = () => show((current - 1 + slides.length) % slides.length, { mode: 'slide', dir: false });
      const next = () => show((current + 1) % slides.length, { mode: 'slide', dir: true });
      card.querySelector('[data-card-prev]')?.addEventListener('click', (e) => { e.preventDefault(); prev(); });
      card.querySelector('[data-card-next]')?.addEventListener('click', (e) => { e.preventDefault(); next(); });
      if (!card.hasAttribute('data-gallery-no-touch') && LG.matches) {
        let touchX = null;
        galleryScroll.addEventListener('touchstart', (e) => { touchX = e.touches[0].clientX; }, { passive: true });
        galleryScroll.addEventListener('touchend', (e) => {
          if (touchX === null) return;
          const d = e.changedTouches[0].clientX - touchX;
          if (Math.abs(d) > SWIPE) { if (d < 0) next(); else prev(); }
          touchX = null;
        });
      }
      card.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      });
      if (LG.matches) {
        // Preview the 2nd own image on card hover; back to the first on leave.
        card.addEventListener('mouseenter', () => show(1, { mode: 'fade' }));
        card.addEventListener('mouseleave', () => show(0, { mode: 'fade' }));
      } else if (V.observeScroll) {
        V.observeScroll(galleryScroll, slides, (idx) => {
          current = idx;
          if (counter) counter.textContent = String(idx + 1);
        });
      }
    }

    if (V.bindSwatches) V.bindSwatches(card, swatches, counterWrap);

    // makeDrawer is only safe with a real drawer + trigger; OOS / no-quick-add
    // cards have neither (renderer skips quick-add), so guard the call —
    // otherwise it throws and aborts init for every card after this one.
    let closeDrawer = function () {};
    if (trigger && trigger.hasAttribute('data-quick-add-direct')) {
      // Direct-add trigger (default-variant products, in stock): add the single
      // variant straight to bag, stamping the card with its ticket for the
      // single notify (like a size pill) — no drawer.
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const doAdd = () => {
          const ticket = trigger.getAttribute('data-voyado-ticket-value');
          if (ticket) {
            card.setAttribute('data-voyado-ticket', ticket);
            const clear = () => { card.removeAttribute('data-voyado-ticket'); };
            document.addEventListener('cart:added', clear, { once: true });
            document.addEventListener('cart:error', clear, { once: true });
          }
          card.dispatchEvent(new CustomEvent('product-card:add', {
            bubbles: true,
            detail: { variantId: trigger.dataset.variantId, card },
          }));
        };
        // Pre-order → route to the PDP instead of adding (voyado-card-quick-add.js fetches product.js to
        // read the selling-plan signal). Non-pre-order / error → normal add.
        const qa = window.BroraVoyadoCardQuickAdd;
        const handle = card.getAttribute('data-product-handle');
        if (qa && qa.gatePreorder && handle) qa.gatePreorder(handle, trigger.dataset.variantId, doAdd);
        else doAdd();
      });
    } else if (trigger && drawer && V.makeDrawer) {
      const d = V.makeDrawer(card, drawer, trigger, sizeBtns);
      closeDrawer = d.close;
      trigger.addEventListener('click', (e) => { e.preventDefault(); d.open({ viaKeyboard: e.detail === 0 }); });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && card.hasAttribute('data-quick-add-open')) { e.preventDefault(); d.close(); }
      });
      card.addEventListener('mouseleave', () => {
        if (card.hasAttribute('data-quick-add-open')) d.close({ returnFocus: false });
      });
    }

    sizeBtns.forEach((btn) => {
      if (btn.hasAttribute('aria-disabled')) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const doAdd = () => {
          // Copy the chosen variant's ticket onto the card root so the single
          // cart:added notify (voyado-client.js, which reads
          // detail.trigger.dataset.voyadoTicket — add-to-bag sets trigger = card)
          // fires once for this add. Strip it again when the add settles so a
          // later mousedown inside the card can't fire a stray click-notify.
          const ticket = btn.getAttribute('data-voyado-ticket-value');
          if (ticket) {
            card.setAttribute('data-voyado-ticket', ticket);
            const clear = () => { card.removeAttribute('data-voyado-ticket'); };
            document.addEventListener('cart:added', clear, { once: true });
            document.addEventListener('cart:error', clear, { once: true });
          }
          card.dispatchEvent(new CustomEvent('product-card:add', {
            bubbles: true,
            detail: { variantId: btn.dataset.variantId, card },
          }));
          closeDrawer();
        };
        // Pre-order → route to the PDP instead of adding (voyado-card-quick-add.js fetches product.js to
        // read the selling-plan signal). Non-pre-order / error → normal add.
        const qa = window.BroraVoyadoCardQuickAdd;
        const handle = card.getAttribute('data-product-handle');
        if (qa && qa.gatePreorder && handle) qa.gatePreorder(handle, btn.dataset.variantId, doAdd);
        else doAdd();
      });
    });

    if (wishlist && window.BroraProductCardWishlist?.bind) {
      window.BroraProductCardWishlist.bind(wishlist);
    }
  }

  function safeInit(card) {
    try { initCard(card); } catch (e) { console.error('[voyado] card init failed', e, card); }
  }

  function initAll(root = document) {
    if (root.matches && root.matches('[data-voyado-card]')) { safeInit(root); return; }
    root.querySelectorAll('[data-voyado-card]').forEach(safeInit);
  }

  // Inserters (PLP controller grid, drawer autocomplete results + trending)
  // dispatch voyado:cards-rendered with the inserted root; init the new cards
  // (the READY guard skips any already bound).
  document.addEventListener('voyado:cards-rendered', (e) => {
    initAll((e.detail && e.detail.root) || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
